<template>
  <FormGroup :label="label" :inputId="idCalc" :errors="errorsCalc" :successes="successes">
    <div v-if="prepend" class="input-group-prepend">
      <span class="input-group-text">{{ prepend }}</span>
    </div>
    <input
      class="form-control"
      :class="{ 'is-invalid': errorsCalc, 'is-valid': successes }"
      :data-test="testId"
      :id="idCalc"
      :name="name"
      :placeholder="placeholder"
      :type="type"
      :value="value"
      :disabled="disabled"
      @input="emitInput"
      @change="emitChange"
    />
    <div v-if="append" class="input-group-append">
      <span class="input-group-text">{{ append }}</span>
    </div>
    <template #append-content>
      <div v-if="passwordMeter" class="password-strength-container">
        <div class="password-strength">
          <div class="password-strength-unit password-strength-0">
            <div
              class="password-strength-color"
              :class="{
                red: passwordColor === 'red',
                yellow: passwordColor === 'yellow',
                green: passwordColor === 'green',
              }"
            ></div>
          </div>
          <div class="password-strength-unit password-strength-1">
            <div
              class="password-strength-color"
              :class="{
                red: passwordColor === 'red' && passwordStrength > 1,
                yellow: passwordColor === 'yellow',
                green: passwordColor === 'green',
              }"
            ></div>
          </div>
          <div class="password-strength-unit password-strength-2">
            <div
              class="password-strength-color"
              :class="{
                green: passwordColor === 'green',
              }"
            ></div>
          </div>
          <div class="password-strength-unit password-strength-3">
            <div
              class="password-strength-color"
              :class="{
                green: passwordColor === 'green' && passwordStrength > 3,
              }"
            ></div>
          </div>
        </div>
        <div class="password-help-tip">
          <Popover
            :header="`PASSWORD TIPS`"
            :message="`<div class='use-tips'> <h6>Use:</h6> <ul> <li>Long passwords.</li> <li>LastPass or Nexcess’s password generator.</li> <li>Random words, punctuation, numbers and symbols.</li> </ul> </div> <div class='dont-use-tips'> <h6>Do Not Use:</h6> <ul> <li>A short password.</li> <li>A word repeated several times.</li> <li>A password you used in other sites.</li> <li>Passwords that can easily be identified with you or your social media accounts.</li> <li>Common keyboard patterns like: '1234' or 'abcdefg'.</li> </ul> </div>`"
          >
            <Icon icon="help" color="epsilon" />
          </Popover>
          <span class="password-tip-text" :class="[passwordColor]">{{ passwordTip }}</span>
        </div>
      </div>
    </template>
  </FormGroup>
</template>

<script>
import { computed } from "@vue/composition-api";
import FormGroup from "@/components/display/form/FormGroup";
import FormMixin from "@/components/display/form/FormMixin";
import Icon from "@/components/display/icon/Icon";
import { passwordStrength as passwordStrengthRule } from "leatherman-js/utils/formatters/password";
import Popover from "@/components/display/Popover";

export default {
  name: "FormInput",
  components: {
    FormGroup,
    Icon,
    Popover,
  },
  mixins: [FormMixin],
  props: {
    append: {
      type: String,
    },
    prepend: {
      type: String,
    },
    type: {
      type: String,
      default: "text",
    },
    value: [String, Number],
    passwordMeter: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const passwordStrength = computed(() => {
      return passwordStrengthRule(props.value);
    });

    const passwordTip = computed(() => {
      const strength = passwordStrengthRule(props.value);

      if (!props.value) {
        return "Enter Password";
      } else if (strength === 0) {
        return "Very Weak";
      } else if (strength === 1) {
        return "Very Weak";
      } else if (strength === 2) {
        return "Weak";
      } else if (strength === 3) {
        return "Strong";
      } else {
        return "Very Strong";
      }
    });

    const passwordColor = computed(() => {
      const strength = passwordStrengthRule(props.value);

      if (strength === 0) {
        return "grey";
      } else if (strength === 1) {
        return "red";
      } else if (strength === 2) {
        return "yellow";
      } else if (strength === 3) {
        return "green";
      } else {
        return "green";
      }
    });

    const emitInput = (e) => {
      emit("input", e.target.value);
    };

    const emitChange = (e) => {
      emit("change", e.target.value);
    };

    return {
      emitChange,
      emitInput,
      passwordStrength,
      passwordTip,
      passwordColor,
    };
  },
};
</script>

<style lang="scss" scoped>
.password-strength-container {
  .password-help-tip {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: row-reverse;

    .password-tip-text {
      transition: color 1s ease;
      font-size: 0.75rem;
      margin-right: 0.5rem;
    }
  }
  .password-strength {
    display: flex;
    flex-direction: row;
    height: 3px;
    margin-left: -5px;
    margin-right: -5px;
    margin-top: 5px;

    .password-strength-unit {
      flex: 0 0 25%;
      max-width: 25%;
      height: 3px;
      padding-right: 5px;
      padding-left: 5px;
      width: 100%;

      .password-strength-color {
        transition: background-color 1s ease;
        width: 100%;
        height: 100%;
      }
    }
  }
}
</style>
